import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { of } from 'rxjs';
import { ColumnDef } from '@tanstack/react-table';
import { capitalCase } from 'change-case';
import { NumberUtils, RxUtils, Tr } from '@utils';
import { FindAllQueryDto, PortalRoutePath, SortOrder, getPortalRoute, sortingMapperToQuery, sortingMapperToTable } from '@models';
import { useFindAllQuery, useService } from '@hooks';
import { CarModel, carModelService, notificationService } from '@services';
import { Autocomplete, Box, Grid, Stack, TextField } from '@mui/material';
import { DataTable, EntityDeleteConfirmationDialog, RouterLinkButton, RouterLinkIconButton } from '@components/common';
import { PortalPageTitle, GuardedPage, VehicleImage } from '@components/portal/common';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

const paddingX = 9;
type Entity = CarModel;

export const CarModels: FC = () => {
	const { t } = useTranslation('portal');
	const { query, setPagination, setSorting, setFiltering } = useFindAllQuery<Entity>({
		page: 0,
		pageSize: 100,
		sort: [{ by: 'brand', order: SortOrder.ASC }]
	});
	const [selectedBrand, setSelectedBrand] = useState<Entity>();
	const [selectedModel, setSelectedModel] = useState<Entity>();
	const [deletingEntities, setDeletingEntities] = useState<Entity[]>([]);
	const { data: carModels, trigger } = useService(() => carModelService.getAllPaginated(query), [query]);
	const { data: brands = [] } = useService(() => carModelService.getAllBrands(), []);
	const { data: models = [] } = useService(
		() => (selectedBrand ? carModelService.getAllModels(selectedBrand.brand) : of([])),
		[selectedBrand]
	);

	const columns = useMemo<ColumnDef<Entity>[]>(
		() => [
			{
				id: 'image',
				size: 80,
				enableSorting: false,
				header: () => '',
				cell: ({ row }) => <VehicleImage model={row.original} style={{ height: '100%', maxHeight: 80, width: 'auto' }} />
			},
			{
				accessorKey: 'id',
				size: 0.01,
				enableSorting: true,
				header: () => <Tr.Portal path="car-models.id" />,
				cell: ({ row }) => row.original.id
			},
			{
				accessorKey: 'brand',
				size: 0.01,
				enableSorting: true,
				header: () => <Tr.Portal path="car-models.brand" />,
				cell: ({ row }) => row.original.brand
			},
			{
				accessorKey: 'model',
				size: 0.01,
				enableSorting: true,
				header: () => <Tr.Portal path="car-models.model" />,
				cell: ({ row }) => row.original.model
			},
			{
				accessorKey: 'motorFuelType',
				size: 0.01,
				enableSorting: true,
				header: () => <Tr.Portal path="car-models.fuel" />,
				cell: ({ row }) => capitalCase(row.original.motorFuelType)
			},
			{
				accessorKey: 'version',
				enableSorting: false,
				header: () => <Tr.Portal path="car-models.version" />,
				cell: ({ row }) => row.original.version
			},
			{
				accessorKey: 'price',
				size: 0.01,
				enableSorting: true,
				meta: { oneLine: true },
				header: () => <Tr.Portal path="car-models.price" />,
				cell: ({ row }) => NumberUtils.formatAsPrice(row.original.price, { decimalScale: 0, pureDefault: true, defaultValue: '-' })
			},
			{
				accessorKey: 'carSegment',
				size: 0.01,
				enableSorting: false,
				meta: { oneLine: true },
				header: () => <Tr.Portal path="car-models.car-segment" />,
				cell: ({ row }) => row.original.carSegment
			},
			{
				accessorKey: 'body',
				enableSorting: false,
				header: () => <Tr.Portal path="car-models.body" />,
				cell: ({ row }) => row.original.body
			},
			{
				id: 'actions',
				size: 0.01,
				enableSorting: false,
				header: () => '',
				cell: ({ row }) => (
					<Stack direction="row" spacing={0.5}>
						<RouterLinkIconButton size="small" href={`${getPortalRoute(PortalRoutePath.CAR_MODELS)}/${row.original.id}`}>
							<EditIcon color="inherit" fontSize="small" />
						</RouterLinkIconButton>
						<RouterLinkIconButton size="small" onClick={() => setDeletingEntities?.([row.original])}>
							<DeleteForeverIcon color="inherit" fontSize="small" />
						</RouterLinkIconButton>
					</Stack>
				)
			}
		],
		[setDeletingEntities]
	);

	const onDelete = (s: Entity) => {
		RxUtils.promisify(
			carModelService.delete(s),
			() => (notificationService.sendSuccess(t('car-models.delete-success-message')), trigger())
		);
	};
	const onDeleteMultiple = (s: Entity[]) =>
		RxUtils.promisify(
			carModelService.deleteMultiple(s),
			() => (notificationService.sendSuccess(t('car-models.delete-success-message')), trigger())
		);

	useEffect(() => {
		const filters: FindAllQueryDto<Entity>['filter'] = [];
		selectedBrand && filters.push({ by: 'brand', values: [selectedBrand.brand] });
		selectedModel && filters.push({ by: 'model', values: [selectedModel.model] });
		setFiltering(filters);
	}, [selectedBrand, selectedModel]);

	return (
		<GuardedPage canActivate={(user) => user.isAdmin()}>
			<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
				<PortalPageTitle
					paddingX={paddingX}
					title={<Tr.Portal path="car-models.title" />}
					subtitle={<Tr.Portal path="car-models.subtitle" options={{ length: carModels?.total || 0 }} />}
				>
					<RouterLinkButton
						variant="contained"
						size="small"
						startIcon={<AddCircleOutlineIcon />}
						href={`${getPortalRoute(PortalRoutePath.CAR_MODELS)}/add`}
					>
						<Tr.Portal path="car-models.add-car-model" />
					</RouterLinkButton>
				</PortalPageTitle>
				<Box sx={{ paddingX }}>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Autocomplete
								isOptionEqualToValue={(option, value) => option.brand === value.brand}
								getOptionLabel={(option) => (typeof option === 'string' ? option : option.brand)}
								options={brands}
								fullWidth
								onChange={(e, value) => {
									setSelectedBrand(value || undefined);
									setSelectedModel(undefined);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										name="brand"
										placeholder={t('stock.add-edit-stock.brand')}
										required
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6}>
							<Autocomplete
								isOptionEqualToValue={(option, value) => option.model === value.model}
								getOptionLabel={(option) => (typeof option === 'string' ? option : option.model)}
								options={models}
								fullWidth
								disabled={!selectedBrand}
								onChange={(e, value) => {
									setSelectedModel(value || undefined);
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										name="model"
										placeholder={t('stock.add-edit-stock.model')}
										required
										InputProps={{
											...params.InputProps,
											endAdornment: params.InputProps.endAdornment
										}}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</Box>

				{Array.isArray(carModels?.data) && carModels?.data.length ? (
					<Box sx={{ paddingX, flexGrow: 1, overflow: 'auto' }}>
						<DataTable
							columns={columns}
							data={carModels.data}
							comparator={(d1, d2) => d1.id === d2.id}
							paginationComponentProps={{ rowsPerPageOptions: [10, 25, 50, 100] }}
							paginationType="ssr"
							pageIndex={carModels?.page ?? query.page}
							pageSize={carModels?.pageSize ?? query.pageSize}
							totalCount={carModels?.total || 0}
							onPageChange={setPagination}
							sortingType="ssr"
							sorting={sortingMapperToTable(query.sort)}
							onSortingChange={(newSorting) => setSorting(sortingMapperToQuery(newSorting))}
							headerTypographyProps={{ variant: 'caption' }}
							bodyCellStyle={(cell) => {
								if (cell.column.id === 'image') {
									return { paddingY: 0, paddingX: 1 };
								} else if (cell.column.id === 'actions') {
									return { paddingY: 0, paddingX: 2 };
								}

								return { wordBreak: 'keep-all' };
							}}
						/>

						<EntityDeleteConfirmationDialog
							open={!!deletingEntities?.length}
							content={<Tr.Portal path="car-models.delete-confirmation-message" />}
							buttonProps={{ 'data-testid': 'car-model-delete-confirmation-button' }}
							onClose={() => setDeletingEntities([])}
							onAccept={() => {
								deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
								setDeletingEntities([]);
							}}
						>
							{deletingEntities.map((e) => `"${[e.brand, e.version].join(' ')}"`).join(', ')}
						</EntityDeleteConfirmationDialog>
					</Box>
				) : null}
			</Stack>
		</GuardedPage>
	);
};
